import React, { useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, TextInput, Switch, Linking,
} from 'react-native';
import { Image } from 'react-native';
import Modal from '../components/Modal';
import { showAlert } from '../services/alert';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Icon from 'react-native-vector-icons/Ionicons';
import Toast from 'react-native-toast-message';
import { useFocusEffect } from '@react-navigation/native';
import { getProfileByUserId, submitProfile, getProfilePhotos, deactivateProfile, reactivateProfile, requestProfileDeletion, cancelProfileDeletion, updateProfile, getLinkedInAuthUrl, removeMyLinkedInVerification } from '../services/api';
import { StatusBadge, Button, THEME } from '../components';
import { AUTH0_CONFIG } from '../config';
import { version as APP_VERSION } from '../../package.json';
import { useAuthContext } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { Profile, ProfilePhoto } from '../types';

const toHttps = (url?: string) => url && !url.startsWith('http') ? `https://${url}` : url;

const MyProfileScreen: React.FC<{ navigation: any }> = ({ navigation }) => {
  const { user, logout, setHasProfile } = useAuthContext();
  const insets = useSafeAreaInsets();
  const { lang, toggleLang, t } = useLanguage();
  const [myProfile, setMyProfile] = useState<Profile | null>(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [requestingDeletion, setRequestingDeletion] = useState(false);
  const [cancellingDeletion, setCancellingDeletion] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'settings'>('profile');

  useFocusEffect(
    useCallback(() => {
      if (!user?.sub) {
        setLoading(false);
        return;
      }
      setLoading(true);
      getProfileByUserId(encodeURIComponent(user.sub))
        .then(res => {
          const profile: Profile | null = res.data ?? null;
          setMyProfile(profile);
          // Persist search defaults so HomeScreen + SearchScreen use them immediately
          if (profile?.gender === 'MALE' || profile?.gender === 'FEMALE') {
            const opposite = profile.gender === 'MALE' ? 'FEMALE' : 'MALE';
            AsyncStorage.setItem('pref_looking_for', opposite);
          }
          if (profile?.religion) {
            AsyncStorage.setItem('pref_search_religion', profile.religion);
          }
          if (profile?.caste) {
            AsyncStorage.setItem('pref_search_caste', profile.caste);
          }
          if (profile?.id) {
            getProfilePhotos(profile.id)
              .then(pr => {
                const photos: ProfilePhoto[] = Array.isArray(pr.data) ? pr.data : [];
                const main = photos.find(p => p.mainPhoto) ?? photos[0];
                // Prefer thumbnail for the small card display — much faster
                setProfilePhotoUrl(toHttps(main?.thumbnailUrl ?? main?.photoUrl) ?? null);
              })
              .catch(() => {});
          }
        })
        .catch((err: Error & { status?: number }) => {
          if (err?.status !== 404) {
            Toast.show({ type: 'error', text1: 'Failed to load your profile' });
          }
          setMyProfile(null);
          setProfilePhotoUrl(null);
        })
        .finally(() => setLoading(false));
    }, [user]),
  );

  const handleDeactivate = async () => {
    if (!myProfile) return;
    setDeactivating(true);
    setShowDeactivateModal(false);
    try {
      await deactivateProfile(myProfile.id);
      setMyProfile(p => p ? { ...p, profileStatus: 'INACTIVE' } : null);
      Toast.show({ type: 'info', text1: t('msg.profileDeactivated', 'Profile Deactivated'), text2: t('msg.profileDeactivatedSub', 'Your profile is now hidden') });
    } catch (e) {
      Toast.show({ type: 'error', text1: 'Failed', text2: (e as Error).message });
    } finally {
      setDeactivating(false);
    }
  };

  const handleReactivate = async () => {
    if (!myProfile) return;
    setReactivating(true);
    try {
      await reactivateProfile(myProfile.id);
      setMyProfile(p => p ? { ...p, profileStatus: 'ACTIVE' } : null);
      Toast.show({ type: 'success', text1: t('msg.profileReactivated', 'Profile Reactivated'), text2: t('msg.profileReactivatedSub', 'Your profile is now visible') });
    } catch (e) {
      Toast.show({ type: 'error', text1: 'Failed', text2: (e as Error).message });
    } finally {
      setReactivating(false);
    }
  };

  const handleRequestDeletion = async () => {
    if (!myProfile) return;
    setRequestingDeletion(true);
    setShowDeleteModal(false);
    setDeleteConfirmText('');
    try {
      const res = await requestProfileDeletion(myProfile.id);
      setMyProfile(res.data as any);
      Toast.show({ type: 'info', text1: t('msg.deletionScheduled', 'Deletion Scheduled'), text2: t('msg.deletionScheduledSub', 'Your profile will be deleted soon') });
    } catch (e) {
      Toast.show({ type: 'error', text1: 'Failed', text2: (e as Error).message });
    } finally {
      setRequestingDeletion(false);
    }
  };

  const handleCancelDeletion = async () => {
    if (!myProfile) return;
    setCancellingDeletion(true);
    try {
      const res = await cancelProfileDeletion(myProfile.id);
      setMyProfile(res.data as any);
      Toast.show({ type: 'success', text1: t('msg.deletionCancelled', 'Deletion Cancelled'), text2: t('msg.deletionCancelledSub', 'Your profile is safe') });
    } catch (e) {
      Toast.show({ type: 'error', text1: 'Failed', text2: (e as Error).message });
    } finally {
      setCancellingDeletion(false);
    }
  };

  const handleConnectLinkedIn = async () => {
    try {
      const res = await getLinkedInAuthUrl();
      await Linking.openURL(res.data.authUrl);
    } catch {
      Toast.show({ type: 'error', text1: 'Could not start LinkedIn verification' });
    }
  };

  const handleDisconnectLinkedIn = () => {
    showAlert(
      'Remove LinkedIn Verification',
      'This will remove your LinkedIn verified badge. You can reconnect anytime. Continue?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            try {
              await removeMyLinkedInVerification();
              setMyProfile(p => p ? { ...p, linkedinVerified: false, linkedinName: undefined } : p);
              Toast.show({ type: 'info', text1: 'LinkedIn verification removed' });
            } catch {
              Toast.show({ type: 'error', text1: 'Could not remove verification' });
            }
          },
        },
      ],
    );
  };

  const handleChangePassword = async () => {
    if (!user?.email) return;
    setChangingPassword(true);
    try {
      const res = await fetch(`https://${AUTH0_CONFIG.domain}/dbconnections/change_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: AUTH0_CONFIG.clientId,
          email: user.email,
          connection: 'Username-Password-Authentication',
        }),
      });
      if (res.ok) {
        Toast.show({ type: 'success', text1: 'Password reset email sent', text2: `Check your inbox at ${user.email}` });
      } else {
        throw new Error('Request failed');
      }
    } catch {
      Toast.show({ type: 'error', text1: 'Could not send reset email', text2: 'Please try again later' });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!myProfile) return;
    setSubmitting(true);
    try {
      await submitProfile(myProfile.id);
      Toast.show({ type: 'success', text1: 'Submitted!', text2: 'Your profile is pending admin review' });
      setMyProfile(p => p ? { ...p, profileStatus: 'PENDING_APPROVAL' } : null);
    } catch (e) {
      Toast.show({ type: 'error', text1: 'Submission failed', text2: (e as Error).message });
    } finally {
      setSubmitting(false);
    }
  };

  const renderProfileSection = () => {
    if (loading) {
      return <ActivityIndicator color={THEME.primary} style={{ marginTop: 20 }} />;
    }

    if (!myProfile) {
      return (
        <View style={styles.noProfile}>
          <Icon name="person-add-outline" size={48} color={THEME.border} />
          <Text style={styles.noProfileText}>{t('msg.noProfileYet', 'No profile yet')}</Text>
          <Button title={t('btn.createMyProfile', 'Create My Profile')} onPress={() => setHasProfile(false)} style={{ marginTop: 16 }} />
        </View>
      );
    }

    const status = myProfile.profileStatus;

    return (
      <View>
        {/* Profile summary row */}
        <View style={styles.profileRow}>
          {profilePhotoUrl
            ? <Image style={styles.profilePhoto} source={{ uri: profilePhotoUrl }} resizeMode="cover" />
            : <View style={[styles.profilePhoto, styles.profilePhotoPlaceholder]}>
                <Text style={styles.profilePhotoInitial}>{myProfile.name?.charAt(0)?.toUpperCase() ?? '?'}</Text>
              </View>
          }
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={styles.profileName}>{myProfile.name}</Text>
            {myProfile.profileCode && (
              <Text style={styles.profileCode}>{myProfile.profileCode}</Text>
            )}
            <Text style={styles.profileSub}>
              {[myProfile.job, myProfile.district, myProfile.stateName].filter(Boolean).join(' • ')}
            </Text>
            <View style={{ marginTop: 6 }}>
              <StatusBadge status={status} />
            </View>
          </View>
        </View>

        {/* PENDING_APPROVAL / ACTIVE with room to grow: completion nudge —
            profiles now go live with only the required fields, so this is
            the ongoing push to fill in the rest (DRAFT's completion bar
            below only ever shows pre-submission, which is now rare). */}
        {(status === 'PENDING_APPROVAL' || status === 'ACTIVE') && (myProfile.completionPercentage ?? 0) < 100 && (
          <View style={styles.nudgeBox}>
            <View style={styles.completionRow}>
              <Text style={styles.completionLabel}>{t('label.profileCompletion', 'Profile Completion')}</Text>
              <Text style={styles.completionPct}>{myProfile.completionPercentage ?? 0}%</Text>
            </View>
            <View style={styles.completionBar}>
              <View style={[styles.completionFill, { width: `${myProfile.completionPercentage ?? 0}%` }]} />
            </View>
            <Text style={styles.nudgeText}>
              {t('msg.completionNudge', 'Complete profiles get noticed more and receive more interest requests. Add a few more details to stand out!')}
            </Text>
            <TouchableOpacity style={[styles.actionBtn, { marginTop: 10 }]} onPress={() => navigation.navigate('EditProfile', { profile: myProfile })}>
              <Icon name="create-outline" size={16} color={THEME.primary} />
              <Text style={styles.actionText}>{t('btn.completeProfile', 'Complete Your Profile')}</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* DRAFT: completion bar + actions */}
        {status === 'DRAFT' && (
          <View style={styles.draftBox}>
            <View style={styles.draftWarning}>
              <Icon name="alert-circle-outline" size={20} color="#92400E" />
              <Text style={styles.draftWarningText}>
                {t('msg.draftWarning', 'Your profile is incomplete and not yet submitted for review. It will not be visible to others until it is submitted and approved by our team.')}
              </Text>
            </View>
            <View style={styles.completionRow}>
              <Text style={styles.completionLabel}>{t('label.profileCompletion', 'Profile Completion')}</Text>
              <Text style={styles.completionPct}>{myProfile.completionPercentage ?? 0}%</Text>
            </View>
            <View style={styles.completionBar}>
              <View style={[styles.completionFill, { width: `${myProfile.completionPercentage ?? 0}%` }]} />
            </View>
            <View style={styles.profileActions}>
              <TouchableOpacity style={styles.actionBtn} onPress={() => navigation.navigate('EditProfile', { profile: myProfile })}>
                <Icon name="pencil-outline" size={16} color={THEME.primary} />
                <Text style={styles.actionText}>{t('btn.continueEditing', 'Continue Editing')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.actionBtn} onPress={() => navigation.navigate('Photo', { profileId: myProfile.id, currentPhoto: myProfile.photoUrl })}>
                <Icon name="camera-outline" size={16} color={THEME.primary} />
                <Text style={styles.actionText}>{t('btn.photo', 'Photo')}</Text>
              </TouchableOpacity>
            </View>
            <Button
              title={t('btn.submitForReview', 'Submit for Review')}
              onPress={handleSubmitForReview}
              loading={submitting}
              style={{ marginTop: 12 }}
            />
          </View>
        )}

        {/* PENDING_APPROVAL: locked but photos still allowed */}
        {status === 'PENDING_APPROVAL' && (
          <View>
            <View style={styles.pendingBox}>
              <Icon name="hourglass-outline" size={28} color={THEME.warning} />
              <Text style={styles.pendingText}>{t('msg.pendingApprovalDetail', 'Your profile is pending admin review')}</Text>
            </View>
            <View style={[styles.profileActions, { marginTop: 12 }]}>
              <TouchableOpacity style={styles.actionBtn} onPress={() => navigation.navigate('Photo', { profileId: myProfile.id, currentPhoto: myProfile.photoUrl })}>
                <Icon name="camera-outline" size={16} color={THEME.primary} />
                <Text style={styles.actionText}>{t('btn.photo', 'Photo')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* ACTIVE: full actions + deactivate */}
        {status === 'ACTIVE' && (
          <View>
            <View style={styles.profileActions}>
              <TouchableOpacity style={styles.actionBtn} onPress={() => navigation.navigate('ProfileDetail', { profileId: myProfile.id })}>
                <Icon name="eye-outline" size={16} color={THEME.primary} />
                <Text style={styles.actionText}>{t('btn.view', 'View')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.actionBtn} onPress={() => navigation.navigate('EditProfile', { profile: myProfile })}>
                <Icon name="pencil-outline" size={16} color={THEME.primary} />
                <Text style={styles.actionText}>{t('btn.edit', 'Edit')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.actionBtn} onPress={() => navigation.navigate('Photo', { profileId: myProfile.id, currentPhoto: myProfile.photoUrl })}>
                <Icon name="camera-outline" size={16} color={THEME.primary} />
                <Text style={styles.actionText}>{t('btn.photo', 'Photo')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* INACTIVE: status notice */}
        {status === 'INACTIVE' && (
          <View style={styles.inactiveBox}>
            <Icon name="pause-circle-outline" size={28} color="#626567" />
            <Text style={styles.inactiveText}>{t('msg.profileInactiveDetail', 'Your profile is currently inactive')}</Text>
            <Text style={{ fontSize: 12, color: THEME.textLight, marginTop: 4 }}>Go to Settings → Account to reactivate.</Text>
          </View>
        )}

        {/* PENDING_DELETION: status notice */}
        {status === 'PENDING_DELETION' && (
          <View style={styles.deletionBox}>
            <Icon name="trash-outline" size={28} color={THEME.danger} />
            <Text style={styles.deletionTitle}>{t('msg.deletionPendingTitle', 'Deletion Pending')}</Text>
            <Text style={styles.deletionText}>
              {t('msg.deletionPendingDetail', 'Your profile will be deleted on')}{' '}
              {myProfile.scheduledDeletionDate
                ? new Date(myProfile.scheduledDeletionDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                : ''}
            </Text>
            <Text style={{ fontSize: 12, color: THEME.textLight, marginTop: 4 }}>Go to Settings → Account to cancel.</Text>
          </View>
        )}

        {/* REJECTED: edit & resubmit */}
        {status === 'REJECTED' && (
          <View style={styles.rejectedBox}>
            <Text style={styles.rejectedText}>{t('msg.profileRejected', 'Your profile was rejected. Please update and resubmit.')}</Text>
            <TouchableOpacity style={[styles.actionBtn, { marginTop: 12 }]} onPress={() => navigation.navigate('EditProfile', { profile: myProfile })}>
              <Icon name="pencil-outline" size={16} color={THEME.primary} />
              <Text style={styles.actionText}>{t('btn.editResubmit', 'Edit & Resubmit')}</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* ── LinkedIn Verification ── */}
        <View style={styles.linkedinCard}>
          <View style={styles.linkedinCardHeader}>
            <Icon name="logo-linkedin" size={18} color="#0A66C2" />
            <Text style={styles.linkedinCardTitle}>LinkedIn Verification</Text>
          </View>
          {myProfile.linkedinVerified ? (
            <View style={styles.linkedinVerifiedRow}>
              <Icon name="checkmark-circle" size={16} color="#059669" />
              <View style={{ flex: 1 }}>
                <Text style={styles.linkedinVerifiedText}>Verified</Text>
                {!!myProfile.linkedinName && (
                  <Text style={styles.linkedinVerifiedName}>{myProfile.linkedinName}</Text>
                )}
              </View>
              <TouchableOpacity onPress={handleDisconnectLinkedIn} style={styles.linkedinDisconnectBtn}>
                <Text style={styles.linkedinDisconnectText}>Disconnect</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity style={styles.linkedinConnectBtn} onPress={handleConnectLinkedIn}>
              <Icon name="logo-linkedin" size={16} color="#fff" />
              <Text style={styles.linkedinConnectText}>Connect LinkedIn</Text>
            </TouchableOpacity>
          )}
          <Text style={styles.linkedinCardSub}>
            {myProfile.linkedinVerified
              ? 'A verified badge is shown to members who unlock your contact details.'
              : 'Add a verified LinkedIn badge to build trust with potential matches.'}
          </Text>
        </View>
      </View>
    );
  };

  return (
    <View style={{ flex: 1 }}>
    {/* Deactivate confirmation modal */}
    <Modal visible={showDeactivateModal} transparent animationType="fade" onRequestClose={() => setShowDeactivateModal(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalBox}>
          <View style={styles.modalIconWrap}>
            <Icon name="pause-circle-outline" size={40} color={THEME.danger} />
          </View>
          <Text style={styles.modalTitle}>{t('modal.deactivateTitle', 'Deactivate Profile?')}</Text>
          <Text style={styles.modalBody}>{t('modal.deactivateBody', 'Your profile will be hidden from other users.')}</Text>
          <View style={styles.modalActions}>
            <TouchableOpacity
              style={styles.modalCancelBtn}
              onPress={() => setShowDeactivateModal(false)}
              activeOpacity={0.8}>
              <Text style={styles.modalCancelText}>{t('btn.cancel', 'Cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalConfirmBtn, deactivating && { opacity: 0.5 }]}
              onPress={handleDeactivate}
              disabled={deactivating}
              activeOpacity={0.85}>
              {deactivating
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text style={styles.modalConfirmText}>{t('btn.deactivate', 'Deactivate')}</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>

    {/* Delete confirmation modal — type "DELETE" to confirm */}
    <Modal visible={showDeleteModal} transparent animationType="fade" onRequestClose={() => setShowDeleteModal(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalBox}>
          <View style={[styles.modalIconWrap, { backgroundColor: '#FDF2F2' }]}>
            <Icon name="trash-outline" size={40} color={THEME.danger} />
          </View>
          <Text style={styles.modalTitle}>{t('modal.deleteTitle', 'Delete Profile?')}</Text>
          <Text style={styles.modalBody}>{t('modal.deleteBody', 'This action is irreversible. Your profile will be permanently deleted.')}</Text>
          <View style={styles.deleteConfirmBox}>
            <Text style={styles.deleteConfirmLabel}>{t('modal.deleteTypeHint', 'Type DELETE to confirm')}</Text>
            <TextInput
              style={styles.deleteConfirmInput}
              value={deleteConfirmText}
              onChangeText={setDeleteConfirmText}
              placeholder="DELETE"
              placeholderTextColor={THEME.border}
              autoCapitalize="characters"
              autoCorrect={false}
            />
          </View>
          <View style={styles.modalActions}>
            <TouchableOpacity
              style={styles.modalCancelBtn}
              onPress={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}
              activeOpacity={0.8}>
              <Text style={styles.modalCancelText}>{t('btn.cancel', 'Cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalConfirmBtn, deleteConfirmText !== 'DELETE' && { opacity: 0.4 }]}
              onPress={handleRequestDeletion}
              disabled={deleteConfirmText !== 'DELETE'}
              activeOpacity={0.85}>
              <Text style={styles.modalConfirmText}>{t('btn.deleteProfile', 'Delete Profile')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>

    <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: 40 }}>
      {/* Header */}
      <View style={[styles.header, { paddingTop: insets.top + 8 }]}>
        <View style={styles.avatarWrap}>
          {(profilePhotoUrl ?? user?.picture)
            ? <Image style={styles.avatar} source={{ uri: (profilePhotoUrl ?? user?.picture)! }} resizeMode="cover" />
            : <View style={[styles.avatar, styles.avatarPlaceholder]}>
                <Text style={styles.avatarInitial}>{myProfile?.name?.charAt(0)?.toUpperCase() ?? '?'}</Text>
              </View>
          }
        </View>
      </View>

      {/* Tab Bar */}
      <View style={styles.tabBar}>
        <TouchableOpacity
          style={[styles.tabBtn, activeTab === 'profile' && styles.tabBtnActive]}
          onPress={() => setActiveTab('profile')}
          activeOpacity={0.8}>
          <Icon name="person-outline" size={16} color={activeTab === 'profile' ? THEME.primary : THEME.textLight} />
          <Text style={[styles.tabBtnText, activeTab === 'profile' && styles.tabBtnTextActive]}>Profile</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tabBtn, activeTab === 'settings' && styles.tabBtnActive]}
          onPress={() => setActiveTab('settings')}
          activeOpacity={0.8}>
          <Icon name="settings-outline" size={16} color={activeTab === 'settings' ? THEME.primary : THEME.textLight} />
          <Text style={[styles.tabBtnText, activeTab === 'settings' && styles.tabBtnTextActive]}>Settings</Text>
        </TouchableOpacity>
      </View>

      {/* ── Profile Tab ── */}
      {activeTab === 'profile' && (
        <>
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('screen.myMatrimonialProfile', 'My Matrimonial Profile')}</Text>
            {renderProfileSection()}
          </View>

          <TouchableOpacity
            style={styles.prefCard}
            onPress={() => navigation.navigate('Preferences')}
            activeOpacity={0.85}>
            <View style={styles.prefCardLeft}>
              <Icon name="options-outline" size={28} color="#fff" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.prefCardTitle}>Match Preferences</Text>
              <Text style={styles.prefCardSub}>Set age, location, caste, profession & more</Text>
            </View>
            <Icon name="chevron-forward" size={20} color={THEME.primary} />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.helpCard}
            onPress={() => navigation.navigate('Help', { profileCode: myProfile?.profileCode })}
            activeOpacity={0.85}>
            <View style={styles.helpCardLeft}>
              <Icon name="help-buoy-outline" size={26} color="#fff" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.helpCardTitle}>{t('screen.helpTitle', 'Help & Support')}</Text>
              <Text style={styles.helpCardSub}>{t('screen.helpSub', 'FAQs, WhatsApp & email support')}</Text>
            </View>
            <Icon name="chevron-forward" size={20} color={THEME.primary} />
          </TouchableOpacity>
        </>
      )}

      {/* ── Settings Tab ── */}
      {activeTab === 'settings' && (
        <>
          {/* Privacy */}
          {myProfile && (
            <View style={styles.settingsCard}>
              <Text style={styles.settingsGroupLabel}>{t('section.privacySettings', 'Privacy')}</Text>
              <View style={styles.settingsRow}>
                <Icon name="images-outline" size={20} color={THEME.text} />
                <View style={{ flex: 1 }}>
                  <Text style={styles.settingsRowText}>{t('label.photoPrivacy', 'Blur Photos')}</Text>
                  <Text style={styles.settingsRowSub}>{t('msg.photoPrivacySub', 'Only accepted connections can see your photos')}</Text>
                </View>
                <Switch
                  value={!!myProfile.photoPrivacy}
                  onValueChange={async val => {
                    setMyProfile(p => p ? { ...p, photoPrivacy: val } : p);
                    try { await updateProfile(myProfile.id, { photoPrivacy: val }); }
                    catch { setMyProfile(p => p ? { ...p, photoPrivacy: !val } : p); Toast.show({ type: 'error', text1: 'Could not update photo privacy' }); }
                  }}
                  trackColor={{ false: THEME.border, true: THEME.primary }}
                  thumbColor="#fff"
                />
              </View>
              <View style={styles.settingsDivider} />
              <View style={styles.settingsRow}>
                <Icon name="call-outline" size={20} color={THEME.text} />
                <View style={{ flex: 1 }}>
                  <Text style={styles.settingsRowText}>{t('label.contactPrivacy', 'Private Contact')}</Text>
                  <Text style={styles.settingsRowSub}>{t('msg.contactPrivacySub', 'Only accepted connections can view your contact')}</Text>
                </View>
                <Switch
                  value={!!myProfile.contactPrivacy}
                  onValueChange={async val => {
                    setMyProfile(p => p ? { ...p, contactPrivacy: val } : p);
                    try { await updateProfile(myProfile.id, { contactPrivacy: val }); }
                    catch { setMyProfile(p => p ? { ...p, contactPrivacy: !val } : p); Toast.show({ type: 'error', text1: 'Could not update contact privacy' }); }
                  }}
                  trackColor={{ false: THEME.border, true: THEME.primary }}
                  thumbColor="#fff"
                />
              </View>
            </View>
          )}

          {/* Preferences */}
          <View style={styles.settingsCard}>
            <Text style={styles.settingsGroupLabel}>Preferences</Text>
            <TouchableOpacity style={styles.settingsRow} onPress={toggleLang} activeOpacity={0.8}>
              <Icon name="language-outline" size={20} color={THEME.text} />
              <Text style={styles.settingsRowText}>Language</Text>
              <Text style={styles.settingsRowValue}>{lang === 'en' ? 'English' : 'हिंदी'}</Text>
              <Icon name="chevron-forward" size={16} color={THEME.textLight} />
            </TouchableOpacity>
            <View style={styles.settingsDivider} />
            <TouchableOpacity style={styles.settingsRow} onPress={() => navigation.navigate('BlockedProfiles')} activeOpacity={0.8}>
              <Icon name="ban-outline" size={20} color={THEME.text} />
              <Text style={styles.settingsRowText}>{t('label.blockedProfiles', 'Blocked Profiles')}</Text>
              <Icon name="chevron-forward" size={16} color={THEME.textLight} />
            </TouchableOpacity>
            <View style={styles.settingsDivider} />
            <TouchableOpacity style={styles.settingsRow} onPress={handleChangePassword} disabled={changingPassword} activeOpacity={0.8}>
              {changingPassword
                ? <ActivityIndicator size="small" color={THEME.text} />
                : <Icon name="key-outline" size={20} color={THEME.text} />}
              <Text style={styles.settingsRowText}>Change Password</Text>
              <Icon name="chevron-forward" size={16} color={THEME.textLight} />
            </TouchableOpacity>
          </View>

          {/* Account */}
          {myProfile && (
            <View style={styles.settingsCard}>
              <Text style={styles.settingsGroupLabel}>Account</Text>
              {myProfile.profileStatus === 'ACTIVE' && (
                <>
                  <TouchableOpacity style={styles.settingsRow} onPress={() => setShowDeactivateModal(true)} activeOpacity={0.8}>
                    <Icon name="pause-circle-outline" size={20} color="#D97706" />
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.settingsRowText, { color: '#D97706' }]}>Deactivate Profile</Text>
                      <Text style={styles.settingsRowSub}>Hide your profile temporarily</Text>
                    </View>
                    <Icon name="chevron-forward" size={16} color={THEME.textLight} />
                  </TouchableOpacity>
                  <View style={styles.settingsDivider} />
                </>
              )}
              {myProfile.profileStatus === 'INACTIVE' && (
                <>
                  <TouchableOpacity style={styles.settingsRow} onPress={handleReactivate} disabled={reactivating} activeOpacity={0.8}>
                    {reactivating
                      ? <ActivityIndicator size="small" color="#059669" />
                      : <Icon name="play-circle-outline" size={20} color="#059669" />}
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.settingsRowText, { color: '#059669' }]}>Reactivate Profile</Text>
                      <Text style={styles.settingsRowSub}>Make your profile visible again</Text>
                    </View>
                    <Icon name="chevron-forward" size={16} color={THEME.textLight} />
                  </TouchableOpacity>
                  <View style={styles.settingsDivider} />
                </>
              )}
              {myProfile.profileStatus === 'PENDING_DELETION' ? (
                <TouchableOpacity style={styles.settingsRow} onPress={handleCancelDeletion} disabled={cancellingDeletion} activeOpacity={0.8}>
                  {cancellingDeletion
                    ? <ActivityIndicator size="small" color={THEME.danger} />
                    : <Icon name="arrow-undo-outline" size={20} color={THEME.danger} />}
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.settingsRowText, { color: THEME.danger }]}>Cancel Deletion</Text>
                    <Text style={styles.settingsRowSub}>Scheduled for {myProfile.scheduledDeletionDate}</Text>
                  </View>
                  <Icon name="chevron-forward" size={16} color={THEME.textLight} />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity style={styles.settingsRow} onPress={() => { setDeleteConfirmText(''); setShowDeleteModal(true); }} disabled={requestingDeletion} activeOpacity={0.8}>
                  {requestingDeletion
                    ? <ActivityIndicator size="small" color={THEME.danger} />
                    : <Icon name="trash-outline" size={20} color={THEME.danger} />}
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.settingsRowText, { color: THEME.danger }]}>{t('btn.deleteProfile', 'Delete Profile')}</Text>
                    <Text style={styles.settingsRowSub}>Permanently remove your account</Text>
                  </View>
                  <Icon name="chevron-forward" size={16} color={THEME.textLight} />
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Sign Out */}
          <View style={styles.settingsCard}>
            <TouchableOpacity style={styles.settingsRow} onPress={logout} activeOpacity={0.8}>
              <Icon name="log-out-outline" size={20} color={THEME.danger} />
              <Text style={[styles.settingsRowText, { color: THEME.danger }]}>Sign Out</Text>
            </TouchableOpacity>
          </View>

          <Text style={styles.versionText}>Jeevan Sanyog v{APP_VERSION}</Text>
        </>
      )}
    </ScrollView>
    </View>
  );
};

export default MyProfileScreen;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: THEME.bg },
  header: { backgroundColor: THEME.primary, paddingTop: 52, paddingBottom: 32, alignItems: 'center' },
  avatarWrap: { position: 'relative', marginBottom: 12, marginTop: 8 },
  avatar: { width: 90, height: 90, borderRadius: 45, borderWidth: 3, borderColor: 'rgba(255,255,255,0.5)' },
  avatarPlaceholder: { alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.25)' },
  avatarInitial: { fontSize: 36, fontWeight: '900', color: '#fff' },
  userName: { fontSize: 22, fontWeight: '900', color: '#fff' },
  userEmail: { fontSize: 15, color: 'rgba(255,255,255,0.75)', marginTop: 4 },
  section: {
    backgroundColor: THEME.card, marginHorizontal: 16, marginTop: 16,
    borderRadius: 16, padding: 20,
    shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 6, elevation: 2,
  },
  sectionTitle: { fontSize: 18, fontWeight: '800', color: THEME.text, marginBottom: 16 },
  profileRow: { flexDirection: 'row', alignItems: 'flex-start' },
  profilePhoto: { width: 64, height: 70, borderRadius: 12, backgroundColor: '#EDE0D8' },
  profilePhotoPlaceholder: { alignItems: 'center', justifyContent: 'center', backgroundColor: THEME.primary },
  profilePhotoInitial: { fontSize: 26, fontWeight: '900', color: '#fff' },
  profileName: { fontSize: 17, fontWeight: '700', color: THEME.text },
  profileCode: { fontSize: 14, fontWeight: '700', color: THEME.primary, marginTop: 2 },
  profileSub: { fontSize: 15, color: THEME.textLight, marginTop: 2 },
  profileActions: { flexDirection: 'row', gap: 8, marginTop: 12 },
  actionBtn: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 6, paddingVertical: 10, borderRadius: 10,
    borderWidth: 1.5, borderColor: THEME.primary, backgroundColor: '#FAE5E5',
  },
  actionText: { color: THEME.primary, fontWeight: '700', fontSize: 15 },
  nudgeBox: {
    backgroundColor: '#FAE5E5', borderRadius: 12,
    padding: 14, marginTop: 16,
  },
  nudgeText: { fontSize: 13, color: THEME.textLight, lineHeight: 19, marginTop: 8 },
  draftBox: { marginTop: 16 },
  draftWarning: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 10,
    backgroundColor: '#FFF3CD', borderRadius: 12,
    padding: 14, marginBottom: 16,
    borderLeftWidth: 3, borderLeftColor: '#F59E0B',
  },
  draftWarningText: {
    flex: 1, fontSize: 13, color: '#92400E', lineHeight: 19,
  },
  completionRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 },
  completionLabel: { fontSize: 15, color: THEME.textLight, fontWeight: '600' },
  completionPct: { fontSize: 15, color: THEME.primary, fontWeight: '800' },
  completionBar: { height: 6, backgroundColor: THEME.border, borderRadius: 3, overflow: 'hidden' },
  completionFill: { height: 6, backgroundColor: THEME.primary, borderRadius: 3 },
  pendingBox: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginTop: 16, padding: 14, backgroundColor: '#FEF9E7', borderRadius: 12 },
  pendingText: { flex: 1, fontSize: 15, color: '#856404', lineHeight: 22 },
  rejectedBox: { marginTop: 12 },
  rejectedText: { fontSize: 15, color: THEME.danger, lineHeight: 22 },
  prefCard: {
    flexDirection: 'row', alignItems: 'center', gap: 14,
    marginHorizontal: 16, marginTop: 16,
    backgroundColor: THEME.card, borderRadius: 16, padding: 16,
    borderWidth: 1.5, borderColor: THEME.primary,
    shadowColor: THEME.primary, shadowOpacity: 0.12, shadowRadius: 8, elevation: 3,
  },
  prefCardLeft: {
    width: 48, height: 48, borderRadius: 14, backgroundColor: THEME.primary,
    alignItems: 'center', justifyContent: 'center',
  },
  prefCardTitle: { fontSize: 16, fontWeight: '800', color: THEME.text },
  prefCardSub: { fontSize: 13, color: THEME.textLight, marginTop: 2 },
  helpCard: {
    flexDirection: 'row', alignItems: 'center', gap: 14,
    marginHorizontal: 16, marginTop: 12,
    backgroundColor: THEME.card, borderRadius: 16, padding: 16,
    borderWidth: 1, borderColor: THEME.border,
    shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 6, elevation: 2,
  },
  helpCardLeft: {
    width: 48, height: 48, borderRadius: 14, backgroundColor: '#7B5EA7',
    alignItems: 'center', justifyContent: 'center',
  },
  helpCardTitle: { fontSize: 16, fontWeight: '800', color: THEME.text },
  helpCardSub: { fontSize: 13, color: THEME.textLight, marginTop: 2 },
  noProfile: { alignItems: 'center', paddingVertical: 24, gap: 8 },
  noProfileText: { fontSize: 16, color: THEME.textLight },
  deleteRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, marginHorizontal: 16, marginTop: 20, marginBottom: 8,
    paddingVertical: 13, borderRadius: 14,
    borderWidth: 1.5, borderColor: THEME.danger, backgroundColor: '#FDF2F2',
  },
  deleteRowText: { fontSize: 15, fontWeight: '700', color: THEME.danger },
  settingsCard: {
    marginHorizontal: 16, marginTop: 20,
    backgroundColor: THEME.card, borderRadius: 16,
    borderWidth: 1, borderColor: THEME.border,
    overflow: 'hidden',
  },
  settingsRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingHorizontal: 16, paddingVertical: 14,
  },
  settingsRowText: { flex: 1, fontSize: 15, fontWeight: '600', color: THEME.text },
  settingsRowValue: { fontSize: 14, color: THEME.textLight, marginRight: 4 },
  settingsDivider: { height: 1, backgroundColor: THEME.border, marginLeft: 48 },

  // Deactivate button (inside ACTIVE section)
  deactivateBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 6, marginTop: 10, paddingVertical: 10, borderRadius: 10,
    borderWidth: 1.5, borderColor: THEME.danger, backgroundColor: '#FDF2F2',
  },
  deactivateBtnText: { color: THEME.danger, fontWeight: '700', fontSize: 14 },

  // INACTIVE status box
  inactiveBox: {
    marginTop: 16, alignItems: 'center', gap: 10,
    padding: 16, backgroundColor: '#F8F9FA', borderRadius: 12,
    borderWidth: 1, borderColor: THEME.border,
  },
  inactiveText: { fontSize: 14, color: '#626567', textAlign: 'center', lineHeight: 20 },
  reactivateBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    marginTop: 4, paddingHorizontal: 24, paddingVertical: 12,
    borderRadius: 12, backgroundColor: THEME.primary,
  },
  reactivateBtnText: { color: '#fff', fontWeight: '800', fontSize: 15 },

  // Confirmation modal
  modalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center', alignItems: 'center', padding: 24,
  },
  modalBox: {
    backgroundColor: '#fff', borderRadius: 20, padding: 24,
    width: '100%', maxWidth: 360, alignItems: 'center',
    shadowColor: '#000', shadowOpacity: 0.15, shadowRadius: 20, elevation: 10,
  },
  modalIconWrap: {
    width: 72, height: 72, borderRadius: 36,
    backgroundColor: '#FDF2F2', alignItems: 'center', justifyContent: 'center',
    marginBottom: 16,
  },
  modalTitle: { fontSize: 19, fontWeight: '900', color: THEME.text, marginBottom: 10, textAlign: 'center' },
  modalBody: { fontSize: 14, color: THEME.textLight, textAlign: 'center', lineHeight: 21, marginBottom: 24 },
  modalActions: { flexDirection: 'row', gap: 10, width: '100%' },
  modalCancelBtn: {
    flex: 1, paddingVertical: 13, borderRadius: 12,
    borderWidth: 1.5, borderColor: THEME.border, alignItems: 'center',
  },
  modalCancelText: { fontSize: 15, fontWeight: '700', color: THEME.text },
  modalConfirmBtn: {
    flex: 1, paddingVertical: 13, borderRadius: 12,
    backgroundColor: THEME.danger, alignItems: 'center',
  },
  modalConfirmText: { fontSize: 15, fontWeight: '800', color: '#fff' },

  // Delete type-to-confirm
  deleteConfirmBox: { width: '100%', marginBottom: 20 },
  deleteConfirmLabel: { fontSize: 13, color: THEME.textLight, marginBottom: 8, textAlign: 'center' },
  deleteConfirmInput: {
    borderWidth: 2, borderColor: THEME.danger, borderRadius: 10,
    paddingHorizontal: 16, paddingVertical: 10,
    fontSize: 16, fontWeight: '800', color: THEME.danger,
    textAlign: 'center', letterSpacing: 3,
    backgroundColor: '#FDF2F2',
  },

  // PENDING_DELETION box
  deletionBox: {
    marginTop: 16, alignItems: 'center', gap: 8,
    padding: 16, backgroundColor: '#FDF2F2', borderRadius: 12,
    borderWidth: 1.5, borderColor: THEME.danger,
  },
  deletionTitle: { fontSize: 15, fontWeight: '800', color: THEME.danger },
  deletionText: { fontSize: 13, color: THEME.danger, textAlign: 'center', lineHeight: 20 },
  cancelDeletionBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    marginTop: 4, paddingHorizontal: 20, paddingVertical: 10,
    borderRadius: 10, borderWidth: 1.5, borderColor: THEME.primary, backgroundColor: '#fff',
  },
  cancelDeletionBtnText: { color: THEME.primary, fontWeight: '700', fontSize: 14 },
  versionText: {
    textAlign: 'center', fontSize: 12, color: THEME.textLight,
    marginTop: 24, marginBottom: 8,
  },
  linkedinCard: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 16,
    marginTop: 14,
    borderWidth: 1,
    borderColor: '#E0E7FF',
  },
  linkedinCardHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
  linkedinCardTitle: { fontSize: 15, fontWeight: '700', color: '#0A66C2', flex: 1 },
  linkedinVerifiedRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, marginBottom: 10 },
  linkedinVerifiedText: { fontSize: 14, fontWeight: '700', color: '#059669' },
  linkedinVerifiedName: { fontSize: 13, color: '#374151', marginTop: 2 },
  linkedinConnectBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#0A66C2',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignSelf: 'flex-start',
    marginBottom: 10,
  },
  linkedinConnectText: { fontSize: 14, fontWeight: '700', color: '#fff' },
  linkedinCardSub: { fontSize: 12, color: '#6B7280', lineHeight: 18 },
  linkedinDisconnectBtn: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8, borderWidth: 1, borderColor: THEME.border },
  linkedinDisconnectText: { fontSize: 12, color: THEME.textLight, fontWeight: '600' },

  // Tab bar
  tabBar: {
    flexDirection: 'row',
    backgroundColor: THEME.card,
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 12,
    padding: 4,
    shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 4, elevation: 2,
  },
  tabBtn: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 6, paddingVertical: 10, borderRadius: 10,
  },
  tabBtnActive: { backgroundColor: '#F3E8FF' },
  tabBtnText: { fontSize: 14, fontWeight: '600', color: THEME.textLight },
  tabBtnTextActive: { color: THEME.primary },

  // Settings group label & row sub-text
  settingsGroupLabel: {
    fontSize: 11, fontWeight: '700', color: THEME.textLight,
    textTransform: 'uppercase', letterSpacing: 0.6,
    paddingHorizontal: 16, paddingTop: 14, paddingBottom: 4,
  },
  settingsRowSub: { fontSize: 12, color: THEME.textLight, marginTop: 1 },
});
